import React from 'react';
import { Menu, Bell, User, Search, LogOut, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';

interface HeaderProps {
  onMenuToggle: () => void;
  isSidebarOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ onMenuToggle, isSidebarOpen }) => {
  const navigate = useNavigate();
  const user = authService.getUser();
  const companyName = authService.getCompanyName();
  const getInitials = (name: string) => {
    if (!name) return 'CA';
    const words = name
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .filter(Boolean);
    const initials = (words[0]?.[0] || '') + (words[1]?.[0] || '');
    return (initials || 'CA').toUpperCase();
  };
  const brandInitials = getInitials(companyName || 'Comercial Admin');

  const goToCompanySelect = () => {
    navigate('/selecionar-empresa');
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  return (
    <header className="bg-white shadow-berry border-b border-gray-100 sticky top-0 z-40 backdrop-blur-sm">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Menu Toggle e Logo */}
        <div className="flex items-center space-x-4">
          <button
            onClick={onMenuToggle}
            className="p-2 rounded-berry text-gray-500 hover:text-primary-600 hover:bg-primary-50 lg:hidden transition-all duration-200"
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="hidden lg:block">
            <button
              type="button"
              onClick={goToCompanySelect}
              title="Selecionar/alterar empresa"
              className="flex items-center space-x-3 group"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-berry flex items-center justify-center shadow-berry">
                <span className="text-white font-bold text-sm">{brandInitials}</span>
              </div>
              <span className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent group-hover:from-primary-700 group-hover:to-primary-500 transition-colors">
                {companyName || 'Comercial Admin'}
              </span>
            </button>
          </div>
        </div>

        {/* Barra de Pesquisa removida conforme solicitação */}

        {/* Ações do Usuário */}
        <div className="flex items-center space-x-2">
          {/* Botão de Pesquisa Mobile removido */}

          {/* Notificações */}
          <button className="p-2 rounded-berry text-gray-500 hover:text-primary-600 hover:bg-primary-50 relative transition-all duration-200 group">
            <Bell className="h-5 w-5" />
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center">
              <span className="h-1.5 w-1.5 bg-white rounded-full"></span>
            </span>
            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary-500 rounded-full animate-ping opacity-75"></span>
          </button>

          {/* Perfil do Usuário */}
          <div className="relative group">
            <button className="flex items-center space-x-3 p-2 rounded-berry text-gray-700 hover:bg-gray-50 transition-all duration-200 border border-transparent hover:border-gray-200">
              <div className="h-9 w-9 bg-gradient-to-br from-primary-500 to-primary-700 rounded-berry flex items-center justify-center shadow-berry">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-sm font-semibold text-gray-900">{user?.name || 'Usuário'}</p>
                <p className="text-xs text-primary-600 font-medium">Admin</p>
              </div>
            </button>

            {/* Dropdown Menu */}
            <div className="absolute right-0 mt-3 w-56 bg-white rounded-berry shadow-berry-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="py-3">
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                  <p className="text-sm font-semibold text-gray-900">{user?.name || 'Usuário'}</p>
                  <p className="text-xs text-gray-500">{user?.email || 'admin@exemplo.com'}</p>
                  <div className="mt-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-700">Admin</span>
                  </div>
                </div>
                <div className="py-2">
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Meu Perfil</span>
                  </button>
                  <button className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-700 transition-all duration-200 flex items-center space-x-2">
                    <Settings className="h-4 w-4" />
                    <span>Configurações</span>
                  </button>
                </div>
                <div className="border-t border-gray-100 pt-2">
                  <button 
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-all duration-200 flex items-center space-x-2"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sair</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
